import {
  applyRangeSelection,
  replaceSelectionKey,
  toggleSelectionKey,
} from '@affine/core/components/selection-checkbox/range-selection';
import type {
  AffineDNDDocTrashCapability,
  AffineDNDEntity,
  AffineDNDFolderTrashCapability,
  AffineDNDSource,
} from '@affine/core/types/dnd';
import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface NavigationPanelTreeContextData {
  /**
   * The level of the current tree node.
   */
  level: number;
  /**
   * Registers a direct child node for empty-state rendering.
   */
  registerChild: () => () => void;
}

export const NavigationPanelTreeContext =
  React.createContext<NavigationPanelTreeContextData | null>(null);

export type NavigationPanelTreeSelectionItem = {
  docTrash?: AffineDNDDocTrashCapability;
  entity: Extract<AffineDNDEntity, { type: 'doc' | 'folder' }>;
  folderTrash?: AffineDNDFolderTrashCapability;
  from?: AffineDNDSource;
  label?: string;
};

export type NavigationPanelTreeSelectionValue = {
  anchorKey: string | null;
  clearSelection: () => void;
  deselectItems: (items: NavigationPanelTreeSelectionItem[]) => void;
  getDragSelection: (
    item: NavigationPanelTreeSelectionItem
  ) => NavigationPanelTreeSelectionItem[];
  isSelected: (key: string) => boolean;
  registerItem: (item: NavigationPanelTreeSelectionItem) => () => void;
  selectedCount: number;
  selectedItems: NavigationPanelTreeSelectionItem[];
  selectedKeys: Set<string>;
  selectionMode: boolean;
  toggleSelection: (
    item: NavigationPanelTreeSelectionItem,
    options?: { range?: boolean; additive?: boolean }
  ) => void;
};

export const NavigationPanelTreeSelectionContext =
  React.createContext<NavigationPanelTreeSelectionValue | null>(null);

export function navigationPanelTreeSelectionKey(
  item: NavigationPanelTreeSelectionItem
) {
  const source = item.from;
  const sourceKey = source
    ? 'nodeId' in source
      ? `${source.at}:${source.nodeId}`
      : source.at
    : 'unknown-source';
  return `${sourceKey}:${item.entity.type}:${item.entity.id}`;
}

function queryVisibleSelectionKeys(): string[] {
  return Array.from(
    document.querySelectorAll<HTMLElement>('[data-navigation-tree-select-key]')
  )
    .map(element => element.dataset.navigationTreeSelectKey)
    .filter((key): key is string => !!key);
}

function isNavigationPanelSelectionPreservingTarget(
  target: EventTarget | null
) {
  return (
    target instanceof Element &&
    !!target.closest(
      '[data-navigation-tree-select-key], [data-navigation-panel-selection-ui="true"]'
    )
  );
}

export const NavigationPanelTreeSelectionProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [selectedItemsByKey, setSelectedItemsByKey] = useState<
    Map<string, NavigationPanelTreeSelectionItem>
  >(new Map());
  const [anchorKey, setAnchorKey] = useState<string | null>(null);
  const registeredItemsByKeyRef = useRef<
    Map<string, NavigationPanelTreeSelectionItem>
  >(new Map());

  const clearSelection = useCallback(() => {
    setSelectedItemsByKey(new Map());
    setAnchorKey(null);
  }, []);

  const deselectItems = useCallback(
    (items: NavigationPanelTreeSelectionItem[]) => {
      if (items.length === 0) {
        return;
      }
      const keys = new Set(items.map(navigationPanelTreeSelectionKey));
      setSelectedItemsByKey(previous => {
        let changed = false;
        const next = new Map(previous);
        for (const key of keys) {
          changed = next.delete(key) || changed;
        }
        return changed ? next : previous;
      });
      setAnchorKey(previous =>
        previous && keys.has(previous) ? null : previous
      );
    },
    []
  );

  const registerItem = useCallback<
    NavigationPanelTreeSelectionValue['registerItem']
  >(item => {
    const key = navigationPanelTreeSelectionKey(item);
    registeredItemsByKeyRef.current.set(key, item);
    return () => {
      const current = registeredItemsByKeyRef.current.get(key);
      if (current === item) {
        registeredItemsByKeyRef.current.delete(key);
      }
      setSelectedItemsByKey(previous => {
        if (!previous.has(key)) {
          return previous;
        }
        const next = new Map(previous);
        next.delete(key);
        return next;
      });
      setAnchorKey(previous => (previous === key ? null : previous));
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearSelection();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [clearSelection]);

  useEffect(() => {
    if (selectedItemsByKey.size === 0) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      if (isNavigationPanelSelectionPreservingTarget(event.target)) {
        return;
      }
      clearSelection();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [clearSelection, selectedItemsByKey.size]);

  const toggleSelection = useCallback<
    NavigationPanelTreeSelectionValue['toggleSelection']
  >(
    (item, options) => {
      const key = navigationPanelTreeSelectionKey(item);
      const currentKeys = Array.from(selectedItemsByKey.keys());
      const result = options?.range
        ? applyRangeSelection({
            anchorKey,
            orderedKeys: queryVisibleSelectionKeys(),
            selectedKeys: currentKeys,
            targetKey: key,
          })
        : options?.additive
          ? toggleSelectionKey({
              selectedKeys: currentKeys,
              targetKey: key,
            })
          : replaceSelectionKey(key);

      setSelectedItemsByKey(previous => {
        const next = new Map<string, NavigationPanelTreeSelectionItem>();
        for (const selectionKey of result.selectedKeys) {
          const selectionItem =
            selectionKey === key
              ? item
              : (registeredItemsByKeyRef.current.get(selectionKey) ??
                previous.get(selectionKey));
          if (selectionItem) {
            next.set(selectionKey, selectionItem);
          }
        }
        return next;
      });
      setAnchorKey(result.anchorKey);
    },
    [anchorKey, selectedItemsByKey]
  );

  const value = useMemo<NavigationPanelTreeSelectionValue>(() => {
    const selectedKeys = new Set(selectedItemsByKey.keys());
    const selectedItems = Array.from(selectedItemsByKey.values());
    return {
      anchorKey,
      clearSelection,
      deselectItems,
      getDragSelection: item => {
        const key = navigationPanelTreeSelectionKey(item);
        return selectedItemsByKey.has(key) && selectedItems.length > 1
          ? selectedItems
          : [item];
      },
      isSelected: key => selectedKeys.has(key),
      registerItem,
      selectedCount: selectedItems.length,
      selectedItems,
      selectedKeys,
      selectionMode: selectedItems.length > 0,
      toggleSelection,
    };
  }, [
    anchorKey,
    clearSelection,
    deselectItems,
    registerItem,
    selectedItemsByKey,
    toggleSelection,
  ]);

  return (
    <NavigationPanelTreeSelectionContext.Provider value={value}>
      {children}
    </NavigationPanelTreeSelectionContext.Provider>
  );
};
